/**
 * AdminUsersTab Component
 *
 * Admin interface for managing users.
 * Displays user list with search, filter, and actions.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, RefreshCw, Users, UserPlus } from 'lucide-react';
import { api } from '@/services/api/client';
import { User, UserRole, UserStatus } from '@/types';
import { UserCard } from './UserCard';
import { EditUserDialog } from './EditUserDialog';
import * as errorUtils from '@/utils/errors';

// Users response data is returned in the standard ApiResponse format
// with users in response.data and pagination in response.pagination

interface UserStatistics {
  totalUsers: number;
  activeUsers: number;
  newUsersThisMonth: number;
  roleDistribution: Record<string, number>;
}

export const AdminUsersTab: React.FC = () => {
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const limit = 20;

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (searchQuery) {
        // Use search endpoint if there's a search query
        const response = await api.get<User[]>(`/users/search?q=${encodeURIComponent(searchQuery)}`);
        let filteredUsers = response.data || [];

        // Apply role and status filters
        if (roleFilter !== 'all') {
          filteredUsers = filteredUsers.filter((u) => u.role === roleFilter);
        }
        if (statusFilter !== 'all') {
          filteredUsers = filteredUsers.filter((u) => u.status === statusFilter);
        }

        setUsers(filteredUsers);
        setTotalPages(1);
        setTotalUsers(filteredUsers.length);
      } else {
        const response = await api.get<User[]>(`/users?${params.toString()}`);
        let fetchedUsers = response.data || [];

        // Apply role and status filters on client side
        if (roleFilter !== 'all') {
          fetchedUsers = fetchedUsers.filter((u) => u.role === roleFilter);
        }
        if (statusFilter !== 'all') {
          fetchedUsers = fetchedUsers.filter((u) => u.status === statusFilter);
        }

        setUsers(fetchedUsers);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalUsers(response.pagination?.total || fetchedUsers.length);
      }
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to load users');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchQuery, roleFilter, statusFilter]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await api.get<UserStatistics>('/users/statistics');
      setStatistics(response.data ?? null);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  }, []);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, roleFilter, statusFilter]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    await fetchUsers();
    await fetchStatistics();
  }, [fetchUsers, fetchStatistics]);

  // Handle edit user
  const handleEditUser = useCallback((user: User) => {
    setSelectedUser(user);
    setShowEditDialog(true);
  }, []);

  // Handle edit success
  const handleEditSuccess = useCallback(() => {
    handleRefresh();
    setSelectedUser(null);
  }, [handleRefresh]);

  // Handle status change
  const handleStatusChange = useCallback(
    async (user: User, newStatus: UserStatus) => {
      try {
        await api.put(`/users/${user.id}/status`, { status: newStatus });
        await fetchUsers();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to update user status');
        alert(errorMessage);
      }
    },
    [fetchUsers]
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Users</h2>
            <p className="text-sm text-muted-foreground">
              Manage platform users, roles, and statuses.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Statistics */}
        {statistics && (
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-muted">
              <div className="text-2xl font-bold">{statistics.totalUsers}</div>
              <div className="text-xs text-muted-foreground">Total Users</div>
            </div>
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
              <div className="text-2xl font-bold text-green-600">{statistics.activeUsers}</div>
              <div className="text-xs text-muted-foreground">Active Users</div>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <div className="text-2xl font-bold text-blue-600">{statistics.newUsersThisMonth}</div>
              <div className="text-xs text-muted-foreground">New This Month</div>
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <div className="text-2xl font-bold">{statistics.roleDistribution?.admin || 0}</div>
              <div className="text-xs text-muted-foreground">Admins</div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mt-4 flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name, email, or username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value={UserRole.ADMIN}>Admin</SelectItem>
              <SelectItem value={UserRole.USER}>User</SelectItem>
              <SelectItem value={UserRole.VIEWER}>Viewer</SelectItem>
              <SelectItem value={UserRole.MODERATOR}>Moderator</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value={UserStatus.ACTIVE}>Active</SelectItem>
              <SelectItem value={UserStatus.INACTIVE}>Inactive</SelectItem>
              <SelectItem value={UserStatus.SUSPENDED}>Suspended</SelectItem>
              <SelectItem value={UserStatus.PENDING}>Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-40">
            <div className="text-muted-foreground">Loading users...</div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="p-4 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            <div className="font-medium">Error loading users</div>
            <div className="text-sm mt-1">{error}</div>
            <Button variant="outline" size="sm" className="mt-2" onClick={handleRefresh}>
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && users.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-center">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <div className="text-lg font-medium">
              {searchQuery || roleFilter !== 'all' || statusFilter !== 'all'
                ? 'No users found'
                : 'No users yet'}
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              {searchQuery || roleFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Users will appear here when they register.'}
            </div>
          </div>
        )}

        {/* User list */}
        {!isLoading && !error && users.length > 0 && (
          <>
            <div className="space-y-4">
              {users.map((user) => (
                <UserCard
                  key={user.id}
                  user={user}
                  onEdit={() => handleEditUser(user)}
                  onStatusChange={(status) => handleStatusChange(user, status)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {users.length} of {totalUsers} users
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Dialog */}
      <EditUserDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        user={selectedUser}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default AdminUsersTab;
